import React from 'react';
import PropTypes from 'prop-types';
import GridContainer from 'components/UI/Grid/GridContainer';
import GridItem from 'components/UI/Grid/GridItem';
import { Trans, withTranslation } from 'react-i18next';
import withStyles from '@material-ui/core/styles/withStyles';
import FormattedDate from 'helpers/dateHelper';
import { formatRoute } from 'react-router-named-routes';
import Button from 'components/UI/CustomButtons/Button';
import { paymentStatus, orderTypes } from 'config';
import Snackbars from 'components/UI/Snackbar/Snackbar';
import WithPermission from 'components/User/WithPermission';
import ArrowBack from '@material-ui/icons/ArrowBack';
import RouterContext from 'helpers/routerContext';
import ModalConfirmation from 'components/common/ModalConfirmation';
import { PAID_AD, USER, PAYMENT } from 'routes/paths';

const styles = {
  wrapper: {
    '& p': {
      fontSize: 16,
    },
  },
  contentContainer: {
    backgroundColor: '#fff',
    marginTop: 16,
    borderRadius: '8px',
    padding: '16px 32px',
    boxShadow: 'rgba(0,0,0,.1) 0px 2px 8px',
    position: 'relative',
    '& h6': {
      marginBottom: 5,
    },
  },
  row: {
    marginTop: 24,
    '& p, & a': {
      display: 'block',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
    },
  },
};

const canRefund = [paymentStatus.PAYED];

class PaymentDetails extends React.PureComponent {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    this.state = {
      showModal: false,
    };
  }

  handleRefund = (isTotal = false) => {
    const { onCreditRefund, data, onRefundPaidAd } = this.props;
    const isCredit = data.order.orderType === orderTypes.CREDIT;

    if (isCredit) {
      const isNegativeCredit =
        isTotal && data.order.agency.credit < data.amount;

      if (isNegativeCredit) {
        return this.setState({ showModal: true });
      }

      return onCreditRefund({ paymentId: data.id, total: isTotal });
    }

    // in case is PaidAd
    return onRefundPaidAd(data.order.paidAd.id, isTotal);
  };

  handleContinue = () => {
    const { onCreditRefund, data } = this.props;
    const paymentId = data.id;
    onCreditRefund({ paymentId, total: true });
    this.setState({ showModal: false });
  };

  getUserLink = order => {
    if (order.agency) {
      // eslint-disable-next-line max-len
      return `${PAID_AD.LIST}?manager=${order.agency.id}&managerName=${order.agency.fullname}`;
    }
    // eslint-disable-next-line max-len
    return `${PAID_AD.LIST}?owner=${order.paidAd.owner.id}&ownerName=${order.paidAd.owner.fullname}`;
  };

  renderPartialRefundBtn = () => {
    const { data, t } = this.props;
    if (
      data.order.orderType === orderTypes.CREDIT ||
      (Boolean(data.order.paidAd) && Boolean(data.order.paidAd.consumedCredit))
    ) {
      return (
        <Button
          color="primary"
          default
          onClick={() => this.handleRefund()}
          style={{
            marginRight: 16,
          }}
          data-testid="btn-partial-refund"
        >
          {t('payment.details.partial')}
        </Button>
      );
    }
    return null;
  };

  render() {
    const {
      classes,
      t,
      data,
      serverErrors,
      onCloseToast,
      success,
    } = this.props;

    const hadError = serverErrors ? Object.keys(serverErrors).length : false;
    const { showModal } = this.state;
    const renderActions =
      canRefund.includes(data.status) && data.type === 'MOLLIE';

    return (
      <>
        <Snackbars
          place="tc"
          message={
            hadError
              ? 'No se pudo realizar la operación'
              : 'Operación realizada'
          }
          open={!!hadError || success}
          color={success ? 'success' : 'danger'}
          close
          closeNotification={onCloseToast}
        />

        {data.order.orderType === orderTypes.CREDIT && (
          <ModalConfirmation
            type="warning"
            show={showModal}
            confirm={this.handleContinue}
            cancel={() => this.setState({ showModal: false })}
            title={<span>Crédito negativo</span>}
            description={
              <Trans
                i18nKey="payment.details.modal.message"
                count={(
                  data.amount -
                  data.order.agency.credit +
                  (data.order.agency.commissionLead / 100) *
                    (data.amount - data.order.agency.credit)
                ).toFixed(2)}
              >
                <a
                  style={{
                    color: 'blue',
                  }}
                  href={this.getUserLink(data.order)}
                  target="__blank"
                >
                  reembolso de algunos autorenovables
                </a>
              </Trans>
            }
            confirmLabel={t('payment.details.modal.continue')}
          />
        )}

        <GridContainer justify="center">
          <GridItem xs={12} md={12} lg={1}>
            <Button
              color="white"
              default
              justIcon
              onClick={() => {
                const history = this.context;
                history.push(PAYMENT.LIST);
              }}
              round
              style={{
                margin: '20px 0 10px',
              }}
            >
              <ArrowBack />
            </Button>
          </GridItem>
          <GridItem xs={12} md={12} lg={8}>
            <h3 className={classes.title}>{t('payment.details.title')}</h3>

            <GridContainer className={classes.wrapper}>
              <GridItem
                xs={9}
                sm={12}
                md={renderActions ? 7 : 12}
                lg={renderActions ? 7 : 12}
              >
                <div className={classes.contentContainer}>
                  <GridContainer>
                    <GridItem xs={12} md={6}>
                      <div className={classes.row}>
                        <h6>ID</h6>
                        <p data-testid="payment-id">{data.id}</p>
                      </div>
                      <div className={classes.row}>
                        <h6>{t('payment.list.headers.externalId')}</h6>
                        <p>{data.platformPaymentId}</p>
                      </div>
                      <div className={classes.row}>
                        <h6>{t('payment.details.status')}</h6>
                        <p>{data.status}</p>
                      </div>
                      <div className={classes.row}>
                        <h6>{t('payment.details.order_type')}</h6>
                        <p>{data.type}</p>
                      </div>
                      {data.amount && (
                        <div className={classes.row}>
                          <h6>{t('payment.details.amount_payment')}</h6>
                          <p>
                            {data.amount} {data.currency}
                          </p>
                        </div>
                      )}
                      <div className={classes.row}>
                        <h6>{t('payment.list.headers.product')}</h6>
                        <p>{data.order.orderType}</p>
                      </div>
                      {data.refundedOn && (
                        <GridContainer>
                          <GridItem>
                            <h6>{t('payment.details.refund')}</h6>
                            <p>{FormattedDate(data.refundedOn, t)}</p>
                          </GridItem>
                          <GridItem>
                            <h6>{t('payment.details.refund_amount')}</h6>
                            <p>{data.refundedAmount}</p>
                          </GridItem>
                        </GridContainer>
                      )}
                    </GridItem>
                    <GridItem xs={12} md={6}>
                      {data.createdOn && (
                        <div className={classes.row}>
                          <h6>{t('payment.details.date_created')}</h6>
                          <p>{FormattedDate(data.createdOn, t)}</p>
                        </div>
                      )}
                      {data.updatedOn && (
                        <div className={classes.row}>
                          <h6>{t('payment.details.date_updated')}</h6>
                          <p>{FormattedDate(data.updatedOn, t)}</p>
                        </div>
                      )}
                      <div className={classes.row}>
                        {data.order.orderType === orderTypes.CREDIT ? (
                          <div>
                            <h6>{t('payment.details.agency')}</h6>
                            <p>
                              <a
                                href={formatRoute(USER.DETAILS, {
                                  id: data.order.agency.id,
                                })}
                              >
                                {data.order.agency.fullname} •{' '}
                                {data.order.agency.email}
                                {data.order.agency.phone && (
                                  <> • {data.order.agency.phone}</>
                                )}
                              </a>
                            </p>
                          </div>
                        ) : (
                          <div>
                            <h6>{t('payment.details.agency')}</h6>
                            {data.order.paidAd ? (
                              <a
                                href={formatRoute(USER.DETAILS, {
                                  id: data.order.paidAd.owner.id,
                                })}
                              >
                                {data.order.paidAd.owner.fullname} <br />
                                {data.order.paidAd.owner.email}{' '}
                                {data.order.paidAd.owner.phone}
                              </a>
                            ) : (
                              <p>—</p>
                            )}
                          </div>
                        )}
                      </div>
                      <div className={classes.row}>
                        <h6>{t('payment.details.user')}</h6>
                        {data.user ? (
                          <p>
                            {data.user.fullname} <br /> {data.user.email}
                          </p>
                        ) : (
                          <p>—</p>
                        )}
                      </div>
                      {data.order.paidAd && (
                        <div className={classes.row}>
                          <h6>{t('payment.details.paidad')}</h6>
                          <a
                            href={formatRoute(PAID_AD.DETAILS, {
                              id: data.order.paidAd.id,
                            })}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {t('payment.details.goto_paidad')}
                          </a>
                        </div>
                      )}
                    </GridItem>
                  </GridContainer>
                </div>
              </GridItem>

              <WithPermission alt={null} allowPermissions={['refund_payment']}>
                {/* eslint-disable-next-line max-len */}
                {renderActions && (
                  <GridItem xs={9} sm={12} md={3} lg={4}>
                    <div className={classes.contentContainer}>
                      <h6>{t('payment.details.refund')}</h6>

                      {this.renderPartialRefundBtn()}

                      <Button
                        color="info"
                        default
                        data-testid="total-refund"
                        onClick={() => this.handleRefund(true)}
                      >
                        Total
                      </Button>
                    </div>
                  </GridItem>
                )}
              </WithPermission>
            </GridContainer>
          </GridItem>
        </GridContainer>
      </>
    );
  }
}

/**
 * Payment Details PropTypes
 * @param classes - styles
 * @param data - ad query's results
 * @param t - prop from HOC with string translations
 * @param onPaymentRefund - called when buttons actions got clicked
 * @param onRefundPaidAd - called when paidAd needs to be refund
 * @param serverErrors - object with errors
 * @param success - boolean to know when operation success
 * @param onCloseToast - called when an error occurred and user close it
 */
PaymentDetails.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  data: PropTypes.shape({}).isRequired,
  t: PropTypes.func.isRequired,
  onCreditRefund: PropTypes.func.isRequired,
  onRefundPaidAd: PropTypes.func.isRequired,
  serverErrors: PropTypes.instanceOf(Object),
  success: PropTypes.bool.isRequired,
  onCloseToast: PropTypes.func.isRequired,
};

PaymentDetails.defaultProps = {
  serverErrors: {},
};

// to fix: The withStyles HOC set contextTypes to a children component
// and it have problem with contextType assigned to AdDetails component
// This is a small hack, with intermediary Component to resolved it temporally
const PaymentDetailsWrapper = props => <PaymentDetails {...props} />;

export default withTranslation()(withStyles(styles)(PaymentDetailsWrapper));
